import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getActivePinia } from 'pinia';
import * as authModuleStatic from '@/stores/auth';
import { router } from '@/router';

/**
 * Shared Axios instance for stars-backend calls.
 *
 * Responsibilities:
 *  - Inject `Authorization: Bearer <token>` from the auth store.
 *  - On 401 (for non-auth endpoints), transparently refresh the access
 *    token using the httpOnly refresh cookie and retry the original
 *    request ONCE.
 *  - Single-flight refresh: concurrent 401s share a single in-flight
 *    refresh promise so we never fire /auth/refresh more than once.
 *  - Do NOT retry on 429 / 403 / 5xx / network errors.
 *  - `withCredentials: true` so the browser sends the httpOnly refresh
 *    cookie on /auth/refresh and /auth/logout.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // Forces CORS preflight on any cross-origin request — simple-
    // request CSRF vectors (a victim's browser auto-posting a form
    // across origins with the refresh cookie) can't attach this
    // header, so the preflight blocks them before our handler runs.
    // See docs/auth.md "CSRF defense" for the full rationale.
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Extended request config so the response interceptor can flag a retry
 * without triggering an infinite loop.
 */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * Single-flight refresh guard. While a refresh is in flight, concurrent
 * 401 handlers await this promise instead of firing their own refresh.
 */
let refreshPromise: Promise<void> | null = null;

/**
 * Auth store accessor.
 *
 * Statically imported — the store only touches `http` inside function
 * bodies (never at module scope), so the `http <-> auth` ESM cycle is
 * safe: by the time either function is invoked both modules have
 * finished loading.
 *
 * Going static eliminates a real race: the previous dynamic-import
 * pattern populated `cachedAuthModule` via a fire-and-forget promise,
 * and a request firing very early (the boot `refreshAccessToken` in
 * `main.ts`) could race the import and land without a bearer header.
 */
type AuthModule = typeof import('@/stores/auth');
let authModuleRef: AuthModule = authModuleStatic;

function getAuthStoreSync() {
  if (!getActivePinia()) return null;
  try {
    return authModuleRef.useAuthStore();
  } catch {
    // Pinia not active yet; caller must handle null.
    return null;
  }
}

/**
 * Exposed for tests that need to swap the module reference (e.g. to
 * a freshly re-imported copy between cases).
 */
export function _setAuthModuleForTests(mod: AuthModule | null): void {
  authModuleRef = mod ?? authModuleStatic;
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const url = config.url ?? '';
  // /auth/refresh must never carry a stale bearer — it authenticates
  // purely via the httpOnly refresh cookie.
  if (url.endsWith('/auth/refresh')) {
    if (config.headers) delete config.headers.Authorization;
    return config;
  }

  const store = getAuthStoreSync();
  if (store?.accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${store.accessToken}`;
  }
  return config;
});

http.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as RetriableConfig | undefined;
    const status = err.response?.status;
    const url = original?.url ?? '';

    // Only act on 401. Everything else (403, 429, 5xx, network) is
    // returned to the caller as-is.
    if (status !== 401 || !original) {
      return Promise.reject(err);
    }

    // Batch-2 — Public endpoints (e.g. `/public/budgets/:token`) must
    // NEVER trigger a bearer-token refresh or a /login bounce. These
    // routes are explicitly token-authenticated in the URL itself;
    // treating a 401 here as "session expired" redirects clients /
    // partners who opened the link to login, which both leaks the
    // existence of the URL and loses the original context.
    //
    // Partners flagged the "public budget URL sends me to login" bug
    // during the batch-2 feedback pass — keeping this check in the
    // frontend as a defensive guard even if the backend also changes
    // its response codes (a defense-in-depth).
    if (url.includes('/public/')) {
      return Promise.reject(err);
    }

    // Never attempt to refresh on the auth endpoints themselves —
    // that way lies infinite recursion.
    //
    // Axios allows numeric / boolean header values, so we stringify
    // before comparing. A caller passing `1` (number) or `true` used
    // to slip past the strict `=== '1'` check and trigger a refresh
    // loop on a refresh-tagged request.
    const skipHeader = original.headers?.['X-Skip-Auth-Retry'];
    const skipRequested = skipHeader != null && String(skipHeader) === '1';
    const isAuthEndpoint =
      url.endsWith('/auth/refresh') ||
      url.endsWith('/auth/login') ||
      url.endsWith('/auth/logout') ||
      skipRequested;
    if (isAuthEndpoint) {
      return Promise.reject(err);
    }

    // Only retry once per original request.
    if (original._retried) {
      return Promise.reject(err);
    }
    original._retried = true;

    // Single-flight: share one refresh promise across concurrent 401s.
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const store = getAuthStoreSync();
          if (!store) throw new Error('auth store unavailable');
          await store.refreshAccessToken();
        } finally {
          // Clear regardless of outcome so a later 401 can try again
          // (after the user re-authenticates).
          refreshPromise = null;
        }
      })();
    }

    try {
      await refreshPromise;
    } catch {
      // Refresh failed — clear and bounce to /login, preserving the
      // intended destination so the user lands back where they were.
      //
      // Use the ORIGINAL request's URL (captured when the request
      // was dispatched) rather than `router.currentRoute.value.fullPath`.
      // At the moment a background 401 lands the router may have
      // already navigated elsewhere, which would point `next` at a
      // page the user isn't actually trying to reach.
      getAuthStoreSync()?.clear();
      if (router.currentRoute.value.name !== 'login') {
        const next = original.url ?? router.currentRoute.value.fullPath;
        void router.push({ path: '/login', query: { next } });
      }
      return Promise.reject(err);
    }

    // Refresh succeeded — rebuild auth header from the fresh token.
    const refreshedStore = getAuthStoreSync();
    if (original.headers && refreshedStore?.accessToken) {
      original.headers.Authorization = `Bearer ${refreshedStore.accessToken}`;
    }

    // Retry the original request.
    return http.request(original);
  },
);

/**
 * Exposed only for tests that need to reset the single-flight guard
 * between cases. Do not call from production code.
 */
export function _resetRefreshPromiseForTests(): void {
  refreshPromise = null;
}
